import { action, makeAutoObservable, runInAction } from 'mobx';
import { Device, DeviceType } from '@ixicare/resources';
import { State } from '~@types/store';
import { Schema } from '../components/DeviceConfigForm/schema';

class DevicesStore {
  rootStore: State;
  schema: Schema | null = null;
  // What the held schema was built for. Without it, moving from a Pro device to a Lite one keeps
  // the Pro form, since the load below short-circuits on an already-present schema.
  schemaKey: string | null = null;
  schemaLoading: boolean = false;
  schemaError: Error | null = null;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      rootStore: false,
      loadConfigSchema: action.bound,
    });
    this.rootStore = rootStore;
  }

  // The service owns the form definition: which fields exist, their defaults, options and
  // constraints, all resolved for the device's firmware. Which variant comes back (full or the
  // condensed installer form) is decided by the backend from the caller's profile, so it is not a
  // parameter here.
  async loadConfigSchema(deviceType: DeviceType, swVersion?: string) {
    const key = `${deviceType}:${swVersion ?? ''}`;
    if (this.schemaKey === key && (this.schema || this.schemaLoading)) {
      return this.schema;
    }
    this.schemaKey = key;
    this.schemaLoading = true;
    this.schemaError = null;
    try {
      // `DeviceConfigSchema` and `Schema` describe the same payload from two angles — the wire
      // shape and the renderer's view of it. This is the one place they meet.
      const schema = (await Device.getDeviceConfigSchema({ deviceType, swVersion })) as unknown as Schema;
      runInAction(() => {
        // Drop a response that resolved after we asked for a different device type or firmware.
        if (this.schemaKey !== key) return;
        this.schema = schema;
        this.schemaLoading = false;
      });
      return schema;
    } catch (e) {
      runInAction(() => {
        if (this.schemaKey !== key) return;
        this.schemaError = e as Error;
        this.schemaLoading = false;
      });
      throw e;
    }
  }

  reset() {
    this.schema = null;
    this.schemaKey = null;
    this.schemaLoading = false;
    this.schemaError = null;
  }
}

export default DevicesStore;
